import React, {useState} from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import styled, {css} from 'styled-components';
import {AnchorLink} from 'components/AnchorLink';
import {CloseIcon} from 'components/CloseIcon';
import {MenuIcon} from 'components/MenuIcon';
import {ButtonLink} from 'components/Button';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';
import {links} from 'global.config';
import {MenuItemIcon} from '../utils/MenuItemIcon';
import {Item, SectionHeader} from '../utils/Styled';
import {
  ecommercePayments,
  inPersonPayments,
  omnichannelPayments,
  paymentsSecurity,
  otherFeatures
} from '../constants/products';
import {paymentMethods, ecommercePlatforms} from '../constants/integrations';
import {ecommerceItems, retailInStoreItems, freelancersItems} from '../constants/useCases';

import {Resources} from '../links/Resources';
import {ScreenSizes} from '../../../../types/responsive';
import {InternalPageLink} from 'components/links/Pages';
import {MenuLink} from '../utils/types';

const MenuWrapper = styled.div<{visible: boolean}>`
  @media (max-width: ${ScreenSizes.md}) {
    box-shadow: ${(props) => props.theme.boxShadow};
    background: #ffffff;
    border-radius: ${(props) => props.theme.borderRadius || '8px'};
    position: absolute;
    top: 20px;
    left: 4%;
    right: 4%;
    width: auto;
    margin: auto;
    transform: scale(0.95);
    transform-origin: 100% 0;
    opacity: 0;
    pointer-events: none;
    transition: 250ms;
    transition-property: transform, opacity;
    z-index: 99999;
    ${(props) =>
      props.visible &&
      css`
        transform: scale(1);
        opacity: 1;
        pointer-events: auto;
      `}
  }
`;

const MenuBody = styled.div`
  display: none;
  flex-direction: column;
  list-style: none;
  margin: 16px 16px 0;
  max-height: 80vh;
  overflow: scroll;
  overflow-x: hidden;

  @media (max-width: ${ScreenSizes.md}) {
    display: flex;
  }
`;

const MenuSection = styled.ul`
  padding: 0;
  list-style: none;
  margin: 0;
  gap: 8px;

  li {
    margin-bottom: 24px;
    align-items: start;
  }

  @media (max-width: ${ScreenSizes.md}) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin-bottom: 8px;
  }

  @media (max-width: 340px) {
    grid-template-columns: repeat(1, 100%);
    column-gap: 0;
  }
`;

const MobileMenuButton = styled.a`
  display: none;
  height: 16px;
  @media (max-width: ${ScreenSizes.md}) {
    display: block;
  }
`;

const MenuCloseButton = styled.button`
  display: none;
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  outline: none;
  padding: 0;
  background: none;
  @media (max-width: ${ScreenSizes.md}) {
    display: block;
  }
`;

const MobileCTA = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  a {
    width: 100%;
  }
`;

const MenuFooter = styled.div`
  display: none;
  margin-top: 16px;
  padding-right: 16px;
  padding-left: 16px;

  @media (max-width: ${ScreenSizes.md}) {
    display: block;
  }
`;

const BoldSectionHeader = styled(SectionHeader)`
  @media (max-width: ${ScreenSizes.md}) {
    font-weight: 700;
  }
`;

// ---------- Mobile menu helper components ----------
const AccordionSection: React.FC<{label: React.ReactNode}> = ({label, children}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <SectionHeader
        onClick={() => setOpen(!open)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <span style={{display: 'inline-flex', alignItems: 'center'}}>
          {label as string}
          <span
            style={{
              marginLeft: 8,
              fontSize: 18,
              fontWeight: 'bold',
              lineHeight: 1,
              marginBottom: open ? 8 : 2
            }}>
            {open ? '⌄' : '›'}
          </span>
        </span>
      </SectionHeader>
      {open && <div>{children as any}</div>}
    </>
  );
};

const ProductsMobile: React.FC<{itemClick: VoidFunction}> = ({itemClick}) => {
  const {language} = useI18next();
  const renderItem = (item: MenuLink) => (
    <Item key={item.slug}>
      <MenuItemIcon name={item.icon} />
      {item.slug ? (
        <InternalPageLink slug={item.slug} activeClassName="active" onClick={itemClick}>
          {item.title}
        </InternalPageLink>
      ) : item.to ? (
        <AnchorLink href={item.toByLanguage?.[language] || item.to} target="_blank">
          {item.title}
        </AnchorLink>
      ) : (
        item.title
      )}
    </Item>
  );

  return (
    <>
      <AccordionSection label={<Trans>E-commerce payments</Trans>}>
        <MenuSection>{ecommercePayments.map(renderItem)}</MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>In-person payments</Trans>}>
        <MenuSection>{inPersonPayments.map(renderItem)}</MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>Omnichannel payments</Trans>}>
        <MenuSection>{omnichannelPayments.map(renderItem)}</MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>Payments security</Trans>}>
        <MenuSection>{paymentsSecurity.map(renderItem)}</MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>Other features</Trans>}>
        <MenuSection>{otherFeatures.map(renderItem)}</MenuSection>
      </AccordionSection>
    </>
  );
};

const IntegrationsMobile: React.FC<{itemClick: VoidFunction}> = ({itemClick}) => {
  const {language} = useI18next();
  const renderItem = (item: MenuLink) => (
    <Item key={item.slug}>
      <MenuItemIcon name={item.icon} />
      {item.slug ? (
        <InternalPageLink slug={item.slug} activeClassName="active" onClick={itemClick}>
          {item.title}
        </InternalPageLink>
      ) : item.to ? (
        <AnchorLink href={item.toByLanguage?.[language] || item.to} target="_blank">
          {item.title}
        </AnchorLink>
      ) : (
        item.title
      )}
    </Item>
  );

  return (
    <>
      <AccordionSection label={<Trans>Payment methods</Trans>}>
        <MenuSection>{paymentMethods.map(renderItem)}</MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>E-commerce platforms</Trans>}>
        <MenuSection>{ecommercePlatforms.map(renderItem)}</MenuSection>
      </AccordionSection>
    </>
  );
};

const UseCasesMobile: React.FC<{itemClick: VoidFunction}> = ({itemClick}) => {
  const renderItem = (item: MenuLink, index: number) => (
    <Item key={(item.slug || '') + '-' + index}>
      <MenuItemIcon name={item.icon} />
      {item.slug ? (
        <InternalPageLink slug={item.slug} activeClassName="active" onClick={itemClick}>
          {item.title}
        </InternalPageLink>
      ) : (
        item.title
      )}
    </Item>
  );

  return (
    <>
      <AccordionSection label={<Trans>Online Payments</Trans>}>
        <MenuSection>{ecommerceItems.map((item, index) => renderItem(item, index))}</MenuSection>
      </AccordionSection>
      <AccordionSection
        label={
          <InternalPageLink
            slug="monei-pay/retail-POS-system"
            onClick={itemClick}
            style={{fontWeight: 600}}>
            <Trans>Retail & In-Store Payments</Trans>
          </InternalPageLink>
        }>
        <MenuSection>
          {retailInStoreItems.map((item, index) => renderItem(item, index))}
        </MenuSection>
      </AccordionSection>
      <AccordionSection label={<Trans>Freelancers & Practices</Trans>}>
        <MenuSection>{freelancersItems.map((item, index) => renderItem(item, index))}</MenuSection>
      </AccordionSection>
    </>
  );
};

export const MobileMenu: React.FC<{isOpen: boolean; setOpen: any}> = ({isOpen, setOpen}) => {
  const ref = useOnclickOutside(() => {
    setOpen(false);
  });

  return (
    <>
      <MobileMenuButton
        href="#"
        title="Open mobile navigation"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}>
        <MenuIcon />
      </MobileMenuButton>
      <MenuWrapper visible={isOpen} ref={ref}>
        <MenuCloseButton
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
          }}>
          <CloseIcon />
        </MenuCloseButton>
        <MenuBody>
          <BoldSectionHeader>
            <Trans>Products</Trans>
          </BoldSectionHeader>
          <div style={{marginLeft: 16}}>
            <ProductsMobile itemClick={() => setOpen(false)} />
          </div>

          <BoldSectionHeader>
            <Trans>Integrations</Trans>
          </BoldSectionHeader>
          <div style={{marginLeft: 16}}>
            <IntegrationsMobile itemClick={() => setOpen(false)} />
          </div>

          <BoldSectionHeader>
            <Trans>Businesses we Serve</Trans>
          </BoldSectionHeader>
          <div style={{marginLeft: 16}}>
            <UseCasesMobile itemClick={() => setOpen(false)} />
          </div>

          <BoldSectionHeader>
            <Trans>Resources</Trans>
          </BoldSectionHeader>
          <div style={{marginLeft: 16}}>
            <Resources itemClick={() => setOpen(false)} hideTitle />
          </div>

          <BoldSectionHeader>
            <Trans>Pricing</Trans>
          </BoldSectionHeader>
          <div style={{marginLeft: 16}}>
            <MenuSection>
              <Item>
                <MenuItemIcon name="pricing" />
                <InternalPageLink
                  slug="pricing"
                  activeClassName="active"
                  onClick={() => setOpen(false)}>
                  <Trans>Pricing</Trans>
                </InternalPageLink>
              </Item>
            </MenuSection>
          </div>
        </MenuBody>
        <MenuFooter>
          <MobileCTA>
            <ButtonLink variant="light" href={links.dashboard}>
              <Trans>Dashboard</Trans>
            </ButtonLink>
          </MobileCTA>
        </MenuFooter>
      </MenuWrapper>
    </>
  );
};
